import React, { useState } from 'react';

const QRCodeUpload = ({ onQrCodeUpload, existingQrCode = null }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(existingQrCode);
  const [error, setError] = useState('');

  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dkhwthjov';
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'shreebakers';

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/image\/(jpeg|jpg|png|gif)/)) {
      setError('Please select a valid image file (JPEG, PNG, or GIF)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      // Create form data for Cloudinary upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', uploadPreset);
      formData.append('folder', 'qr_codes');

      // Upload to Cloudinary
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      
      if (data.secure_url) {
        setPreview(data.secure_url);
        onQrCodeUpload(data.secure_url);
        setError('');
      } else {
        throw new Error('No secure URL received');
      }
    } catch (err) {
      console.error('QR Code upload error:', err);
      setError('Failed to upload QR code. Please try again.');
      setPreview(null);
      onQrCodeUpload(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveQrCode = () => {
    setPreview(null);
    onQrCodeUpload(null);
    setError('');
    // Reset file input
    const fileInput = document.getElementById('qr-code-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          QR Code *
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Upload your UPI QR code for withdrawal payments (JPEG, PNG, or GIF, max 5MB)
        </p>
        
        {/* QR Code Preview */}
        {preview ? (
          <div className="mb-4">
            <div className="relative inline-block">
              <img
                src={preview}
                alt="QR Code Preview"
                className="w-32 h-32 object-cover border-2 border-gray-300 rounded-lg"
              />
              <button
                type="button"
                onClick={handleRemoveQrCode}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                disabled={uploading}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-green-600 mt-2">QR code uploaded successfully</p>
          </div>
        ) : (
          <div className="mb-4">
            <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <p className="text-sm text-gray-500 mt-2">No QR code uploaded</p>
          </div>
        )}

        {/* File Input */}
        <div className="flex items-center space-x-4">
          <input
            id="qr-code-input"
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif"
            onChange={handleFileSelect}
            disabled={uploading}
            className="hidden"
          />
          <label
            htmlFor="qr-code-input"
            className={`px-4 py-2 rounded-lg cursor-pointer transition-colors ${
              uploading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {uploading ? 'Uploading...' : preview ? 'Change QR Code' : 'Upload QR Code'}
          </label>
          
          {preview && (
            <button
              type="button"
              onClick={handleRemoveQrCode}
              disabled={uploading}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Remove
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          <span className="text-sm text-gray-600">Uploading QR code...</span>
        </div>
      )}
    </div>
  );
};

export default QRCodeUpload;
